// Angular Files
import {
    Component,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

// Angular Material Files

// Other External Files

// Component Files

// Teller Online Files
import {
    PaymentMethodTypeEnumDto,
    ScheduledPaymentDto,
    ScheduledPaymentInstanceStatusEnumDto,
    ScheduledPaymentIntervalEnumDto,
    ScheduledPaymentStatusEnumDto
} from 'apps/public-portal/src/app/core/api/PublicPortalApiClients';
import {
    formatYyyyMmDd
} from 'apps/public-portal/src/app/core/utils/schedule-date.utils';

// Teller Online Library Files
import { TellerOnlineAppService } from 'teller-online-libraries/core';
import {
    ScheduleDetailsViewModel,
    SchedulePaymentRow
} from 'teller-online-libraries/shared';

@Component({
    selector: 'app-schedule-details-modal',
    templateUrl: './schedule-details-modal.component.html',
    styleUrls: ['./schedule-details-modal.component.scss'],
    standalone: false
})
export class ScheduleDetailsModalComponent implements OnInit, OnChanges {
    // #region fields

    @HostBinding('class') hostClass = 'schedule-details-modal';

    public cancelScheduleForm: FormGroup;

    // #endregion fields

    // #region properties

    @Input() public schedule: ScheduledPaymentDto;

    @Input() public currentPaymentId: number = 0;

    @Input() public backAriaLabel: string = 'Back';

    @Output() public closed = new EventEmitter<void>();

    @Output() public canceled = new EventEmitter<void>();

    @Output() public rowClicked = new EventEmitter<SchedulePaymentRow>();

    // #endregion properties

    // #region cTor

    constructor(
        private formBuilder: FormBuilder,
        public appService: TellerOnlineAppService
    ) { }

    // #endregion cTor

    // #region lifecycle

    ngOnInit() {
        this.cancelScheduleForm = this.formBuilder.group({
            confirmCancel: [false, Validators.requiredTrue]
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.schedule && this.cancelScheduleForm) {
            this.cancelScheduleForm.reset();
        }
    }

    // #endregion lifecycle

    // #region template helpers

    public get title(): string {
        if (!this.schedule?.title) {
            return 'Schedule Details';
        }

        return `Schedule Details for ${this.schedule.title}`;
    }

    public get subtitle(): string {
        if (!this.schedule?.instances?.length) {
            return '';
        }

        const sorted = [...this.schedule.instances].sort(
            (a, b) => a.scheduledForDate
                .localeCompare(b.scheduledForDate)
        );
        const count = sorted.length;
        const first = sorted[0];
        const last = sorted[count - 1];

        if (count === 1) {
            return `One-time payment \u00B7 ${
                formatYyyyMmDd(first.scheduledForDate)}`;
        }

        const label = this._frequencyLabel(
            this.schedule.interval
        );

        return `${count} ${label} payments`
            + ` \u00B7 ${formatYyyyMmDd(first.scheduledForDate)}`
            + ` \u2013 ${formatYyyyMmDd(last.scheduledForDate)}`;
    }

    public get canCancel(): boolean {
        if (!this.schedule) {
            return false;
        }

        return this.schedule.status
                === ScheduledPaymentStatusEnumDto.Pending
            || this.schedule.status
                === ScheduledPaymentStatusEnumDto.Active;
    }

    public get isPaymentMethodECheck(): boolean {
        return this.schedule?.paymentMethod?.paymentMethodType
            === PaymentMethodTypeEnumDto.ECheck;
    }

    public get scheduleView(): ScheduleDetailsViewModel {
        if (!this.schedule) {
            return null;
        }

        const instanceRows = this.schedule.instances.map(i => {
            const amount = i.actualAmount ?? i.estimatedAmount;
            const fee = i.actualConvenienceFee
                ?? i.estimatedConvenienceFee
                ?? 0;

            return {
                id: i.instanceNumber,
                status: this._mapInstanceStatus(i.status),
                amount,
                fee,
                total: amount + fee,
                date: i.scheduledForDate,
                paymentId: i.paymentId,
                instanceGuid: i.guid,
                // Any instance with a real guid (executed or
                // unexecuted, including Canceled) is clickable —
                // it appears as a row in /profile/payment-history.
                // Pure projections have no guid → not clickable.
                clickable: i.guid != null,
            };
        });

        const hasAdjustedFinal = instanceRows.length >= 2
            && instanceRows[0].amount
                !== instanceRows[instanceRows.length - 1].amount;

        const payments = instanceRows.map((row, index) => ({
            ...row,
            amountMarker: hasAdjustedFinal
                && index === instanceRows.length - 1
                    ? '**'
                    : undefined,
        }));

        const totalFees = payments.reduce(
            (sum, p) => sum + p.fee, 0
        );
        // TODO (TOP-3034): Restore when estimate display is configurable
        // const feesAreEstimates = this.schedule.instances.some(
        //     i => i.actualConvenienceFee == null
        //         && i.status
        //             !== ScheduledPaymentInstanceStatusEnumDto
        //                 .Canceled
        // );
        const feesAreEstimates = false;

        const totalAmountPaid = this._getTotalAmountPaid();

        return {
            payments,
            totalScheduledAmount:
                this.schedule.totalPaymentAmount,
            originalBalanceDue:
                this.schedule.totalPaymentAmount,
            totalAmountPaid,
            balanceRemaining:
                this.schedule.totalPaymentAmount
                    - totalAmountPaid,
            showFees: true,
            feesAreEstimates,
            totalFees,
            grandTotal:
                this.schedule.totalPaymentAmount + totalFees,
        };
    }

    public onClick_cancel() {
        if (!this.cancelScheduleForm.valid) {
            return;
        }

        this.canceled.emit();
    }

    public on_rowClick(row: SchedulePaymentRow): void {
        if (!row?.clickable) {
            return;
        }

        this.rowClicked.emit(row);
    }

    // #endregion template helpers

    // #region helpers

    private _mapInstanceStatus(
        status: ScheduledPaymentInstanceStatusEnumDto
    ): string {
        switch (status) {
            case ScheduledPaymentInstanceStatusEnumDto
                .CompletedSuccessfully:
                return 'Finalized';
            case ScheduledPaymentInstanceStatusEnumDto
                .NotCompletedSuccessfully:
                return 'Failed';
            case ScheduledPaymentInstanceStatusEnumDto
                .Processing:
                return 'Processing';
            case ScheduledPaymentInstanceStatusEnumDto.Pending:
                return 'Pending';
            case ScheduledPaymentInstanceStatusEnumDto.Active:
                return 'Scheduled';
            case ScheduledPaymentInstanceStatusEnumDto
                .ValidationFailed:
                return 'Failed';
            case ScheduledPaymentInstanceStatusEnumDto.Canceled:
                return 'Canceled';
            default:
                return 'Pending';
        }
    }

    private _frequencyLabel(
        interval: ScheduledPaymentIntervalEnumDto
    ): string {
        switch (interval) {
            case ScheduledPaymentIntervalEnumDto.Weekly:
                return 'weekly';
            case ScheduledPaymentIntervalEnumDto.BiWeekly:
                return 'bi-weekly';
            case ScheduledPaymentIntervalEnumDto.Monthly:
                return 'monthly';
            case ScheduledPaymentIntervalEnumDto.Quarterly:
                return 'quarterly';
            case ScheduledPaymentIntervalEnumDto.SemiAnnually:
                return 'semi-annually';
            case ScheduledPaymentIntervalEnumDto.Annually:
                return 'annually';
            default:
                return '';
        }
    }

    private _getTotalAmountPaid(): number {
        if (!this.schedule) {
            return 0;
        }

        return this.schedule.instances
            .filter(i => i.status
                === ScheduledPaymentInstanceStatusEnumDto
                    .CompletedSuccessfully)
            .reduce(
                (sum, i) =>
                    sum + (i.actualAmount ?? i.estimatedAmount), 0
            );
    }

    // #endregion helpers
}
